"use client"

import { useState, useEffect, useRef, type ReactNode } from "react"
import Box from "@mui/material/Box"

interface IntersectionObserverProps {
  children: ReactNode
  rootMargin?: string
  threshold?: number
  placeholder?: ReactNode
  height?: number | string
}

export default function IntersectionObserver({
  children,
  rootMargin = "200px",
  threshold = 0,
  placeholder,
  height = "auto",
}: IntersectionObserverProps) {
  const [isIntersecting, setIsIntersecting] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting)
      },
      {
        rootMargin,
        threshold,
      },
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current)
      }
    }
  }, [rootMargin, threshold])

  useEffect(() => {
    if (isIntersecting && !hasLoaded) {
      setHasLoaded(true)
    }
  }, [isIntersecting, hasLoaded])

  return (
    <Box
      ref={ref}
      sx={{
        position: "relative",
        minHeight: height,
        width: "100%",
        // Ensure consistent positioning
        display: "block",
        // Remove transition to prevent layout shifts
      }}
    >
      {/* Use absolute positioning for both placeholder and content to prevent layout shifts */}
      {!hasLoaded && (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            // Ensure consistent positioning
            display: "block",
          }}
        >
          {placeholder}
        </Box>
      )}

      {/* Only render children when loaded */}
      {hasLoaded && (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            // Ensure consistent positioning
            display: "block",
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  )
}
